import React from 'react';

import {
    type DataTableCommonProps,
    type DataTableHeadingProps,
    type DataTableProps,
    type DataTableSkeletonCellProps,
} from './DataTable.types';
import { Collection, DataValue, SkeletonCollection, SkeletonDataValue, SortAction } from './components';

import {
    StyledDataTable,
    Table,
    TableBody,
    TableBodyCell,
    TableBodyHeader,
    TableHead,
    TableHeadHeader,
    TableHeadHeaderInterior,
    TableRow,
} from './DataTable.style';

export function DataTable({ children, lastCellAlignEnd = false, separated = false }: DataTableProps) {
    return (
        <StyledDataTable
            data-table-align={lastCellAlignEnd ? 'end' : 'start'}
            data-table-collapse={separated ? 'separate' : 'collapse'}
        >
            <Table>{children}</Table>
        </StyledDataTable>
    );
}

function Row({ children }: DataTableCommonProps) {
    return <TableRow>{children}</TableRow>;
}

function Head({ children }: DataTableCommonProps) {
    return (
        <TableHead>
            <Row>{children}</Row>
        </TableHead>
    );
}

function ColumnHeader<T>({ label, columnId, sortDirection, onSort }: DataTableHeadingProps<T>) {
    const contentMarkup = onSort ? (
        <SortAction label={label} columnId={columnId} sortDirection={sortDirection} onSort={onSort} />
    ) : (
        <SortAction.Label>{label}</SortAction.Label>
    );

    return (
        <TableHeadHeader $sortOffset={Boolean(onSort)}>
            <TableHeadHeaderInterior>{contentMarkup}</TableHeadHeaderInterior>
        </TableHeadHeader>
    );
}

function Body({ children }: DataTableCommonProps) {
    return <TableBody>{children}</TableBody>;
}

function RowHeader({ children }: DataTableCommonProps) {
    return <TableBodyHeader>{children}</TableBodyHeader>;
}

function Cell({ children }: DataTableCommonProps) {
    return <TableBodyCell>{children}</TableBodyCell>;
}

function SkeletonRow({ children }: DataTableCommonProps) {
    return <Row>{children}</Row>;
}

function SkeletonCell({ children, header = false }: DataTableSkeletonCellProps) {
    return header ? <RowHeader>{children}</RowHeader> : <Cell>{children}</Cell>;
}

///
/// Table elements

Head.Header = ColumnHeader;
Row.Header = RowHeader;
Row.Cell = Cell;
Body.Row = Row;

DataTable.Head = Head;
DataTable.Body = Body;

///
/// Content elements

DataTable.Collection = Collection;
DataTable.DataValue = DataValue;

///
/// Skeleton elements

SkeletonRow.SkeletonCell = SkeletonCell;

DataTable.SkeletonRow = SkeletonRow;
DataTable.SkeletonCollection = SkeletonCollection;
DataTable.SkeletonDataValue = SkeletonDataValue;
